import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight, ForumOutlined } from 'Components/Atoms/Icons';
import { SupportConversationDocument } from 'types/shared/documents/support';
import { RestaurantTicketSummary } from 'types/shared/routes/v03/support';
import {
  conversationStatusLabel,
  formatSupportTime,
  ticketStatusHint,
  ticketStatusLabel,
  ticketStatusStep,
  ticketTypeLabel,
} from './helpers';

type Props = {
  ticket: RestaurantTicketSummary;
  /** The conversation this ticket is linked to, if the restaurant has one. */
  conversation?: SupportConversationDocument;
  conversationLoading: boolean;
  onOpenConversation: () => void;
  /** Starts a new conversation about this ticket. */
  onAsk: () => void;
};

const URL_PATTERN = /(https?:\/\/[^\s<>]*[^\s<>.,;:!?)])/g;

const linkify = (text: string) =>
  text.split(URL_PATTERN).map((part, index) =>
    index % 2 ? (
      <a key={index} href={part} target="_blank" rel="noopener noreferrer">
        {part.replace(/^https?:\/\//, '')}
      </a>
    ) : (
      part
    ),
  );

// "Problem" or "Abnahme:" above a paragraph, but not a numbered step, a
// "Browser: Safari" pair or a divider.
const isHeading = (line: string) =>
  line.length <= 48 &&
  !/[.!?,;]$/.test(line) &&
  !/^\d+[.)]\s/.test(line) &&
  (line.endsWith(':') || !/:\s/.test(line)) &&
  /[a-zäöü]/i.test(line) &&
  !line.startsWith('http');

// Descriptions are plain text: blank lines split paragraphs, "- " lines become
// a list, and one short line that opens a paragraph reads as its heading.
const renderDescription = (text: string) => {
  const lines = text.split('\n').map((line) => line.trim());
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let items: string[] = [];
  let afterHeading = false;

  const flush = () => {
    if (paragraph.length)
      blocks.push(
        <p key={blocks.length}>
          {paragraph.map((line, index) => (
            <React.Fragment key={index}>
              {!!index && <br />}
              {linkify(line)}
            </React.Fragment>
          ))}
        </p>,
      );
    if (items.length)
      blocks.push(
        <ul key={blocks.length}>
          {items.map((item, index) => (
            <li key={index}>{linkify(item)}</li>
          ))}
        </ul>,
      );
    paragraph = [];
    items = [];
  };

  lines.forEach((line, index) => {
    const item = line.match(/^[-•]\s+(.*)$/);
    if (item) {
      if (paragraph.length) flush();
      items.push(item[1]);
      afterHeading = false;
      return;
    }
    if (items.length || !line) flush();
    if (!line) {
      afterHeading = false;
      return;
    }
    const heading =
      !paragraph.length &&
      !afterHeading &&
      !!lines[index + 1] &&
      isHeading(line);
    afterHeading = heading;
    if (heading)
      blocks.push(
        <p key={blocks.length} className="support-ticket-detail__desc-heading">
          {linkify(line)}
        </p>,
      );
    else paragraph.push(line);
  });
  flush();
  return blocks;
};

// User-facing ticket info only — internal notes / connectedIssue are never shown.
const TicketDetail = ({
  ticket,
  conversation,
  conversationLoading,
  onOpenConversation,
  onAsk,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const descRef = useRef<HTMLDivElement>(null);
  const revealRef = useRef(0);
  const description = ticket.description || '';
  const step = ticketStatusStep(ticket.status);

  useLayoutEffect(() => {
    const el = descRef.current;
    if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [description, expanded]);

  // The clamped box can still scroll: a link below the fold takes focus,
  // find-in-page jumps into it. onScroll opens it then, and this moves the
  // panel by what the box had scrolled, so the spot stays where it was.
  useLayoutEffect(() => {
    const offset = revealRef.current;
    revealRef.current = 0;
    const panel = descRef.current?.closest('.support-panel__body');
    if (expanded && offset && panel) panel.scrollTop += offset;
  }, [expanded]);

  return (
    <div className="support-ticket-detail">
      <span className="support-ticket-detail__type">
        {ticketTypeLabel(ticket.type)}
      </span>
      <h3 className="support-ticket-detail__title">{ticket.title}</h3>

      <div
        className={`support-ticket-detail__status support-ticket-detail__status--${ticket.status}`}
      >
        <div className="support-ticket-detail__status-row">
          <span className="support-ticket-detail__status-dot" />
          <span className="support-ticket-detail__status-label">
            {ticketStatusLabel(ticket.status)}
          </span>
          <span className="support-ticket-detail__status-time">
            {/* Older tickets carry no statusChangedAt. At the first step that
                is the creation. Later steps show no time: updatedAt also moves
                on internal notes. */}
            {formatSupportTime(
              ticket.statusChangedAt ??
                (step === 1 ? ticket.createdAt : undefined),
            )}
          </span>
        </div>
        <div className="support-ticket-detail__steps" aria-hidden="true">
          {[1, 2, 3, 4].map((n) => (
            <span
              key={n}
              className={`support-ticket-detail__step${
                n <= step ? ' support-ticket-detail__step--on' : ''
              }`}
            />
          ))}
        </div>
        <p className="support-ticket-detail__status-hint">
          {ticketStatusHint(ticket.status)}
        </p>
      </div>

      {!conversationLoading && (
        <div className="support-starters support-ticket-detail__link">
          <button
            type="button"
            className="support-starter"
            onClick={conversation ? onOpenConversation : onAsk}
          >
            <span className="support-starter__icon">
              <ForumOutlined />
            </span>
            <span className="support-starter__text">
              <span className="support-starter__label">
                {conversation ? 'Zum Gespräch' : 'Frage zu diesem Ticket'}
              </span>
              <span className="support-starter__description">
                {conversation
                  ? `${conversationStatusLabel(
                      conversation.status,
                    )} · ${formatSupportTime(
                      conversation.updatedAt ?? conversation.createdAt,
                    )}`
                  : 'Öffnet ein Gespräch zu diesem Ticket'}
              </span>
            </span>
            <ChevronRight className="support-starter__chevron" />
          </button>
        </div>
      )}

      {!!description && (
        <section className="support-ticket-detail__section">
          <h4 className="support-section__title">Beschreibung</h4>
          <div
            ref={descRef}
            className={`support-ticket-detail__desc${
              expanded ? '' : ' support-ticket-detail__desc--clamped'
            }${!expanded && overflows ? ' support-ticket-detail__desc--faded' : ''}`}
            onScroll={({ currentTarget: box }) => {
              if (expanded || !box.scrollTop) return;
              revealRef.current = box.scrollTop;
              setExpanded(true);
            }}
          >
            {renderDescription(description)}
          </div>
          {(overflows || expanded) && (
            <button
              type="button"
              className="support-ticket-detail__more"
              aria-expanded={expanded}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
            </button>
          )}
        </section>
      )}

      <p className="support-ticket-detail__meta">
        Erstellt am {formatSupportTime(ticket.createdAt)}
      </p>
    </div>
  );
};

export default TicketDetail;
